<template>
  <div class="report-table-editor">
    <div class="editor-table-wrap">
      <table
        class="editor-table"
        :style="tableStyle"
      >
        <colgroup>
          <col
            v-for="column in columns"
            :key="`col-${column.key}`"
            :style="{ width: column.width }"
          />
          <col
            v-if="showRemoveColumn"
            :style="{ width: actionColumnPercent }"
          />
        </colgroup>
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="`head-${column.key}`"
              :class="{ 'numeric-header': column.align === 'right' }"
            >
              {{ column.label }}
            </th>
            <th
              v-if="showRemoveColumn"
              class="action-header"
            ></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in localRows"
            :key="`${rowKeyPrefix}-${index}`"
          >
            <td
              v-for="column in columns"
              :key="`${rowKeyPrefix}-${index}-${column.key}`"
              :class="{ 'numeric-cell': column.numeric || column.align === 'right' }"
            >
              <textarea
                v-model="row[column.key]"
                class="cell-input"
                :class="{ 'cell-input-numeric': column.numeric || column.align === 'right' }"
                :placeholder="column.placeholder || ''"
                rows="1"
                @input="handleCellInput"
              ></textarea>
            </td>
            <td
              v-if="showRemoveColumn"
              class="action-cell"
            >
              <button
                type="button"
                class="remove-row-btn"
                aria-label="Remove row"
                @click="removeRow(index)"
              >
                <i class="las la-times"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <BaseButton
      type="button"
      stylevariant="primary"
      :cus_style="addRowButtonStyle"
      @click="addRow"
    >
      <i class="las la-plus"></i>
      {{ addRowLabel }}
    </BaseButton>
  </div>
</template>

<script>
import BaseButton from '@/components/UI/BaseButton.vue';

export default {
  name: 'ReportTableEditor',
  components: {
    BaseButton
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
    },
    emptyRow: {
      type: Object,
      required: true
    },
    addRowLabel: {
      type: String,
      default: 'Add row'
    },
    tableMinWidth: {
      type: String,
      default: ''
    },
    showRemoveColumn: {
      type: Boolean,
      default: true
    },
    rowKeyPrefix: {
      type: String,
      default: 'report-table-row'
    },
    actionColumnPercent: {
      type: String,
      default: '2%'
    }
  },
  data() {
    return {
      localRows: [],
      addRowButtonStyle: {
        width: 'auto',
        padding: '0.35rem 0.6rem',
        marginTop: '0.75rem',
        fontSize: '0.85rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem'
      }
    };
  },
  computed: {
    tableStyle() {
      const style = {};

      if (this.tableMinWidth) {
        style.minWidth = this.tableMinWidth;
      }

      return style;
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(rows) {
        this.syncLocalRows(rows);
      }
    },
    emptyRow: {
      deep: true,
      handler() {
        this.syncLocalRows(this.value);
      }
    }
  },
  methods: {
    createEmptyRow() {
      return { ...this.emptyRow };
    },
    syncLocalRows(rows) {
      const emptyRow = this.createEmptyRow();
      const nextRows = Array.isArray(rows) && rows.length
        ? rows.map((row) => ({ ...emptyRow, ...row }))
        : [emptyRow];

      this.localRows = nextRows;
      this.$nextTick(this.resizeAllCellInputs);
    },
    handleCellInput(event) {
      this.resizeCellInput(event.target);
      this.emitChange();
    },
    resizeCellInput(input) {
      if (!input) return;
      input.style.height = 'auto';
      input.style.height = `${input.scrollHeight}px`;
    },
    resizeAllCellInputs() {
      this.$el.querySelectorAll('.cell-input').forEach(this.resizeCellInput);
    },
    emitChange() {
      this.$emit('input', this.localRows.map((row) => ({ ...row })));
    },
    addRow() {
      this.localRows = [
        ...this.localRows,
        this.createEmptyRow()
      ];
      this.emitChange();
    },
    removeRow(index) {
      if (this.localRows.length <= 1) {
        this.localRows = [this.createEmptyRow()];
        this.emitChange();
        return;
      }

      this.localRows = this.localRows.filter((_, rowIndex) => rowIndex !== index);
      this.emitChange();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.report-table-editor {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.editor-table-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.editor-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border: 1px solid #111827;
    padding: 6px 4px;
    vertical-align: middle;
    text-align: left;
    box-sizing: border-box;
    overflow: hidden;
  }

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    white-space: normal;
    word-break: break-word;
    line-height: 1.25;
    font-size: 12px;
  }

  th.numeric-header,
  td.numeric-cell {
    padding-left: 4px;
    padding-right: 4px;
  }

  .numeric-header {
    text-align: right;
  }

  .action-header,
  .action-cell {
    width: 2%;
    padding: 6px 0 !important;
    text-align: center;
    vertical-align: middle;
  }
}

.cell-input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-height: 34px;
  height: auto;
  padding: 0.4rem 0.45rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  font-family: $brand-font-regular;
  color: #2d3748;
  background: #ffffff;
  box-sizing: border-box;
  line-height: 1.35;
  overflow: hidden;
  resize: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.cell-input-numeric {
  text-align: right;
  padding-left: 0.3rem;
  padding-right: 0.3rem;
  font-size: 12px;
}

.remove-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  i {
    font-size: 1.25rem;
    line-height: 1;
    color: #94a3b8;
    transition: color 0.15s ease;
  }

  &:hover i {
    color: #dc2626;
  }
}
</style>
